import { FC, useId } from 'react';
import { css, keyframes, useTheme } from '@emotion/react';

const rotateAnimation = keyframes`
  0% { transform: rotate(20deg) translate(10px, -8px); };
  100% { transform: rotate(20deg) translate(-91px, -8px); };
`;

const maskStyle = css`
  > path {
    transform: rotate(20deg) translate(10px, -8px);
  }
`;

const maskAnimatedStyle = css`
  > path {
    animation: ${rotateAnimation} 10s infinite linear;
  }
`;

export const Globe: FC<{ size?: number; strokeWidth?: number; color?: string; animate?: boolean }> = ({
  size = 36,
  strokeWidth = 2.3,
  color: colorProp = undefined,
  animate = false,
}) => {
  const id = useId();
  const theme = useTheme();

  let color = colorProp;
  if (!color) color = theme.color.grey[100];

  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 36 36" width={size} height={size}>
      <defs />
      <path
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={strokeWidth}
        d="M18 31.5a13.5 13.5 0 100-27 13.5 13.5 0 000 27z"
      />
      <mask id={id} width="28" height="28" x="4" y="4" mask-type="alpha" maskUnits="userSpaceOnUse">
        <path fill="#000" d="M18 31.5a13.5 13.5 0 100-27 13.5 13.5 0 000 27z" />
      </mask>
      <g mask={`url(#${id})`} css={[maskStyle, animate && maskAnimatedStyle]}>
        <path
          fill={color}
          d="M12.5 33H25c0-2-.5-6-.7-7.5l-7.6-4.7-.4-.2-3.3-.4a1.1 1.1 0 00-1.1.6l-2 4.4a1.1 1.1 0 00.2 1.2l2.7 2.8a1.1 1.1 0 01.3 1l-.6 2.8zM113.5 33H126c0-2-.5-6-.7-7.5l-7.6-4.7-.4-.2-3.3-.4a1.1 1.1 0 00-1.1.6l-2 4.4a1.1 1.1 0 00.2 1.2l2.7 2.8a1.1 1.1 0 01.3 1l-.6 2.8zM3.3 9.3l-1.2 3c-.1.3-.1.6 0 .8l1.6 4.3a1.1 1.1 0 00.8.7l.8.2 1.1-2.8a1.1 1.1 0 011-.7h3l.6-.1 1.7-1 .3-.1 3.7-3.4a1.1 1.1 0 00.3-1.4L15.5 6h-11L3.3 9.3zM104.3 9.3l-1.2 3c-.1.3-.1.6 0 .8l1.6 4.3a1.1 1.1 0 00.8.7l.8.2 1.1-2.8a1.1 1.1 0 011-.7h3l.6-.1 1.7-1 .3-.1 3.7-3.4a1.1 1.1 0 00.3-1.4L116.5 6h-11l-1.2 3.3z"
          opacity=".2"
        />
        <path
          stroke={color}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={strokeWidth}
          d="M12.5 33l.6-2.8a1.1 1.1 0 00-.3-1L10 26.4a1.1 1.1 0 01-.2-1.2l2-4.4a1.1 1.1 0 011.1-.6l3.3.4c.1 0 .3 0 .4.2l7.6 4.7.7 7.5M113.5 33l.6-2.8a1.1 1.1 0 00-.3-1l-2.7-2.8a1.1 1.1 0 01-.2-1.2l2-4.4a1.1 1.1 0 011.1-.6l3.3.4c.1 0 .3 0 .4.2l7.6 4.7.7 7.5"
        />
        <path
          stroke={color}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={strokeWidth}
          d="M5 6l-3 6.3v.8l1.7 4.3a1.1 1.1 0 00.8.7l3 .7a1.1 1.1 0 01.8.6l.5 1.1a1.1 1.1 0 001 .6h2M106 6l-3 6.3v.8l1.7 4.3a1.1 1.1 0 00.8.7l3 .7a1.1 1.1 0 01.8.6l.5 1.1a1.1 1.1 0 001 .6h2"
        />
        <path
          stroke={color}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={strokeWidth}
          d="M15.5 6L17 8.8a1.1 1.1 0 01-.3 1.4L13 13.6l-.3.1-1.7 1-.5.1h-3a1.1 1.1 0 00-1 .7l-1.2 2.8M116.5 6l1.5 2.8a1.1 1.1 0 01-.3 1.4l-3.7 3.4-.3.1-1.7 1-.5.1h-3a1.1 1.1 0 00-1 .7l-1.2 2.8"
        />
        <path
          fill={color}
          d="M70.6 28.3l-1.6 1-3.5-3.8-2.8-8.2-3.7-3.8-3.5-1.5-7-3.5h-5L40 6V2h49v4l-1.3 4.4.2 3.4c0 .3-.1.6-.3.8l-3.1 3.6-.7.4-4.5.6c-.2 0-.5 0-.8-.2l-2.7-2a1.1 1.1 0 00-1.6.3l-2.8 4.4c-.2.1-.2.3-.2.6v5a1.1 1.1 0 01-.6 1z"
          opacity=".2"
        />
        <path
          stroke={color}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={strokeWidth}
          d="M70.6 28.3l-.2.1a2 2 0 01-2.5-.3l-2.1-2.3a2 2 0 01-.4-.7l-2.5-7.4a2 2 0 00-.5-.7l-3.1-3.2a2 2 0 00-.7-.5L55.5 12l-6.6-3.3a2 2 0 00-.9-.2h-3.9a2 2 0 01-1.1-.4l-2.2-1.5A2 2 0 0140 5V4c0-1.1.9-2 2-2h45a2 2 0 012 2v2.3L87.7 10v3.7c0 .3 0 .6-.2.8l-3.1 3.6-.7.4-4.5.6c-.2 0-.5 0-.8-.2l-2.7-2a1.1 1.1 0 00-1.6.3l-2.8 4.4c-.2.1-.2.3-.2.6v5a1.1 1.1 0 01-.6 1z"
        />
        <path
          fill={color}
          d="M86.8 30.6l1.6 2.5v2H98V33l-2-5-3.6-1.2-3.5-3.2a1.1 1.1 0 00-1.2-.2L83.5 25a1.1 1.1 0 00-.7 1l-.4 2.2a1.1 1.1 0 00.9 1.2l3 .8c.2 0 .4.2.5.3z"
          opacity=".2"
        />
        <path
          stroke={color}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={strokeWidth}
          d="M86.8 30.6l1.6 2.5v2H98V33l-2-5-3.6-1.2-3.5-3.2a1.1 1.1 0 00-1.2-.2L83.5 25a1.1 1.1 0 00-.7 1l-.4 2.2a1.1 1.1 0 00.9 1.2l3 .8c.2 0 .4.2.5.3z"
        />
        <path
          fill={color}
          d="M41 13l-4.1 1.8a1 1 0 00-.5.6L35 19.3v.4l.4 3c0 .2 0 .4.2.5l2 2c.2.2.5.3.8.3l4.1-.5c.3 0 .5 0 .7.2l2.4 1.6c.2.1.3.3.4.6l.8 2.9v.4l-.4 2.2v2.6c0 .6.4 1 1 1h12.6c.5 0 1-.4 1-.9l.3-2c0-.4 0-.7-.3-1l-1.6-1.2a1 1 0 01-.4-1l.9-3.9 1-3.2a1 1 0 00-.9-1.3h-.5a1 1 0 01-.9-.5l-1.2-2-2.7-5a1 1 0 00-.8-.5l-4.7-.5a1 1 0 01-.5-.2l-2.3-2a1 1 0 00-1 0L41 13z"
          opacity=".2"
        />
        <path
          stroke={color}
          strokeWidth={strokeWidth}
          d="M41 13l-4.1 1.8a1 1 0 00-.5.6L35 19.3v.4l.4 3c0 .2 0 .4.2.5l2 2c.2.2.5.3.8.3l4.1-.5c.3 0 .5 0 .7.2l2.4 1.6c.2.1.3.3.4.6l.8 2.9v.4l-.4 2.2v2.6c0 .6.4 1 1 1h12.6c.5 0 1-.4 1-.9l.3-2c0-.4 0-.7-.3-1l-1.6-1.2a1 1 0 01-.4-1l.9-3.8v-.2l1-2c.3-.5.2-1.1-.3-1.4l-1.5-.9a1 1 0 01-.3-.3l-1.4-2.3-2.8-4.6a1 1 0 00-.6-.5l-4.9-.9a1 1 0 01-.4-.1l-2.4-1.6a1 1 0 00-.9-.2L41 13z"
        />
      </g>
    </svg>
  );
};
